import { Language } from "@/data/model/Enums";
import type { IFileInfo } from "@/data/model/System";
import { Misc } from "@/data/util/Misc";

import { i18n } from "@/portal/Trans";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";

export interface IFileUploadOptions {
    multiLanguage?: boolean;
    allowedTypes?: Array<string>;
    maxFileSizeInBytes?: number;
}

export class FileUpload {
    private $element: JQuery;
    private $inner: JQuery;
    private $input: JQuery;
    private cfg?: IFileUploadOptions;

    //cache values for single language upload
    private pendingUpload?: File;
    private hasRemovedFile = false;

    public onChange?: { (value?: File) };

    private $flags?: JQuery;

    //cache values for multi langauge upload
    private pendingUploads?: Array<File | undefined>;
    private hasRemovedFiles?: Array<boolean>;
    private cachedInfos?: Array<IFileInfo | undefined>;
    private activeLang?: Language;

    constructor(cfg?: IFileUploadOptions) {
        this.cfg = cfg;
        this.$element = $('<div class="fileupload form-control" />');
        this.$input = $('<input type="file" class="form-control" />').appendTo(this.$element);
        this.$input.change(() => {
            this.validateAndSet(this.$input.prop("files"));
        });

        this.$inner = $('<div class="inner" />').appendTo(this.$element);

        if (cfg !== undefined && cfg.multiLanguage === true) {
            this.pendingUploads = [];
            this.cachedInfos = [];
            this.hasRemovedFiles = [];

            this.$flags = $('<div class="flags" />').appendTo(this.$element.addClass("multilanguage"));

            Misc.eachEnum(Language, (i, e) => {
                $('<div class="flag ' + e.toLowerCase() + '" title="' + e + '"></div>')
                    .data("model", i)
                    .appendTo(this.$flags!)
                    .click((e) => {
                        this.changeToLanguage($(e.currentTarget).data("model"));
                    });
            });

            this.changeToLanguage(TastierGlobal.getLang());
        }

        this.setChooseFile();
    }

    public getValue(): File | undefined {
        return this.pendingUpload;
    }

    public getValueForLanguage(lang: Language): File | undefined {
        if (this.cfg !== undefined && this.cfg.multiLanguage === true) return this.pendingUploads![lang];

        return undefined;
    }

    public needsRemoval(): boolean {
        return this.hasRemovedFile;
    }

    public needsRemovalForLanguage(lang: Language): boolean | undefined {
        if (this.cfg !== undefined && this.cfg.multiLanguage === true) return this.hasRemovedFiles![lang];

        return undefined;
    }

    public setValue(info?: IFileInfo): FileUpload {
        if (info === undefined) this.setChooseFile();
        else this.setInfo(info);

        return this;
    }

    public setValueForLanguage(info: IFileInfo, lang: Language): FileUpload | undefined {
        if (this.cfg === undefined || this.cfg.multiLanguage === false) return;

        this.cachedInfos![lang] = info;
        if (lang === this.activeLang) {
            if (info === undefined || info == null) this.setChooseFile();
            else this.setInfo(info);
        }

        return this;
    }

    public setError(hasError: boolean): FileUpload {
        if (hasError) this.$element.addClass("is-invalid");
        else this.$element.removeClass("is-invalid");

        return this;
    }

    public setDisabled(): FileUpload {
        return this;
    }

    public renderTo($element: JQuery): FileUpload {
        this.$element.appendTo($element);
        return this;
    }

    private setChooseFile() {
        this.$inner.empty();
        $('<div class="lbl clickable">' + i18n("elements.fileUpload.noFileSelected") + "</div>")
            .appendTo(this.$inner)
            .click((e) => {
                e.preventDefault();
                this.$input.trigger("click");
            });

        $('<button class="choose action">' + i18n("elements.fileUpload.selectFile") + "</button>")
            .appendTo(this.$inner)
            .click((e) => {
                e.preventDefault();
                this.$input.trigger("click");
            });
    }

    private setPendingUpload(file: File) {
        this.$inner.empty();
        this.$inner.append('<div class="lbl">' + file.name + " - " + Misc.byteToReadableString(file.size) + "</div>");
        $('<button class="reset action"><i class="flaticon2-trash"></i></button>')
            .appendTo(this.$inner)
            .click((e) => {
                e.preventDefault();

                if (this.cfg !== undefined && this.cfg.multiLanguage === true) {
                    this.pendingUploads![this.activeLang!] = undefined;
                    this.cachedInfos![this.activeLang!] = undefined;
                } else this.pendingUpload = undefined;

                this.setChooseFile();
            });

        if (this.cfg !== undefined && this.cfg.multiLanguage === true) {
            this.pendingUploads![this.activeLang!] = file;
            this.hasRemovedFiles![this.activeLang!] = false;
        } else {
            this.pendingUpload = file;
            this.hasRemovedFile = false;
        }

        if (this.onChange != null) this.onChange(file);
    }

    private setInfo(info: IFileInfo) {
        this.$inner.empty();
        $('<div class="lbl clickable">' + info.name + " - " + Misc.byteToReadableString(info.size) + "</div>")
            .appendTo(this.$inner)
            .click((e) => {
                e.preventDefault();
                location.href = info.path;
            });

        $('<button class="reset action"><i class="flaticon2-trash"></i></button>')
            .appendTo(this.$inner)
            .click((e) => {
                e.preventDefault();

                if (this.cfg !== undefined && this.cfg.multiLanguage === true) {
                    this.pendingUploads![this.activeLang!] = undefined;
                    this.cachedInfos![this.activeLang!] = undefined;
                    this.hasRemovedFiles![this.activeLang!] = true;
                } else {
                    this.hasRemovedFile = true;
                    this.pendingUpload = undefined;
                }

                this.setChooseFile();
            });
    }

    private validateAndSet(fileList: FileList) {
        const file = fileList[0];
        if (file === undefined) return;

        if (this.cfg!.allowedTypes !== undefined && this.cfg!.allowedTypes.length > 0) {
            if (!this.supportsFileType(file.type)) {
                let allowed = "";
                for (const i in this.cfg!.allowedTypes) allowed += this.cfg!.allowedTypes[i] + ", ";

                allowed = allowed.substr(0, allowed.length - 2);
                UiNotification.showException(i18n("elements.fileUpload.invalidMime", { allowed: allowed }));
                return;
            }
        }

        if (this.cfg!.maxFileSizeInBytes !== undefined && file.size > this.cfg!.maxFileSizeInBytes) {
            UiNotification.showException(
                i18n("elements.fileUpload.invalidSize", {
                    size: Misc.byteToReadableString(this.cfg!.maxFileSizeInBytes),
                })
            );
            return;
        }

        this.setPendingUpload(file);
    }

    private supportsFileType(fileType: string): boolean {
        fileType = fileType.toLowerCase();
        for (const i in this.cfg!.allowedTypes) {
            if (fileType.indexOf(this.cfg!.allowedTypes[i]) !== -1) return true;
        }

        return false;
    }

    private changeToLanguage(lang: Language) {
        if (this.activeLang === lang) return;

        this.activeLang = lang;
        const newVal = this.pendingUploads![lang];
        this.$flags!.find("> .active").removeClass("active");
        this.$flags!.children().each((i, e) => {
            const el = $(e);
            if (el.data("model") === lang) {
                el.addClass("active");
                return false;
            }
        });

        if (newVal === undefined) {
            //check if info is available
            const info = this.cachedInfos![lang];
            if (info !== undefined) this.setInfo(info);
            else this.setChooseFile();
        } else this.setPendingUpload(newVal);
    }
}
